import { Box, Button, SxProps, TextField, Typography } from "@mui/material";
import { COLOR } from "../../../../utils/colors";
import { shadow } from "../../../../utils/styles";
import { useState } from "react";
import axios from "axios";
import { SearchAddressType } from "./_type";
import { Theme } from "@emotion/react";
import { smoothTransition } from "../../../../utils/animate";

export default function SearchAddress({
  onSelect,
  sx
}: {
  onSelect?: (result: SearchAddressType) => void,
  sx?: SxProps<Theme>
}) {
  const [search, setSearch] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResult, setSearchResult] = useState<SearchAddressType[]>([])
  const [isSearchLoading, setIsSearchLoading] = useState(false)
  const [isResultShown, setIsResultShown] = useState(false)
  const onSearch = async () => {
    if (!search) {
      return
    }
    setIsSearchLoading(true)
    setSearchQuery(search)
    const result = await axios.get(`https://nominatim.openstreetmap.org/search.php?format=jsonv2&q=${encodeURIComponent(search)}`)
    setSearchResult(result.data)
    setIsSearchLoading(false)
    setIsResultShown(true)
  }

  const onLocate = (result: SearchAddressType) => {
    if (result?.lat && result?.lon) {
      onSelect?.(result)
    }
    setSearch('')
    setSearchResult([])
    setIsResultShown(false)
  }

  const getTitle = (address: string) => {
    return address.split(',')[0]
  }

  const getSubtitle = (address: string) => {
    const addressParts = address.split(',')
    if (addressParts.length > 1) {
      return addressParts.slice(1).join(', ')
    }
    return ''
  }

  const onClear = () => {
    setSearch('')
    setSearchResult([])
    setIsResultShown(false)
  }

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      zIndex: 1,
      backgroundColor: COLOR.white,
      padding: '16px',
      rounded: '10px',
      ...shadow,
      ...sx
    }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: '6px', alignItems: 'center', width: '400px', borderRadius: '10px' }}>
        <TextField sx={{ flex: 'auto' }} value={search} onKeyDown={(e) => {
          if(e.code === 'Enter') {
            onSearch()
          }
        }} onChange={(e) => setSearch(e.target.value)} size="small" placeholder="Search Address" />
        <Button onClick={onSearch} disabled={isSearchLoading} variant="outlined" sx={{ flex: 'none', height: '40px', color: COLOR.accent.blue }}>
          {isSearchLoading ? 'Searching...' : 'Search'}
        </Button>
      </Box>

      {isResultShown && (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '300px', overflowY: 'auto', maxWidth: '400px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: '6px', px: '4px', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography color={COLOR.gray[400]}>Result for "<span style={{ color: COLOR.black, fontWeight: 500 }}>{searchQuery}</span>"</Typography>
              <Typography color={COLOR.gray[400]} sx={{ cursor: 'pointer' }} onClick={onClear}>Clear</Typography>
            </Box>
            {searchResult.map((result) => (
              <Box
                onClick={() => onLocate(result)}
                key={result.place_id}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px',
                  cursor: 'pointer',
                  padding: '12px',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: COLOR.gray[100]
                  },
                  ...smoothTransition
                }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 600, color: COLOR.black }}>{getTitle(result.display_name)}</Typography>
                <Typography sx={{ fontSize: '14px', color: COLOR.gray[500] }}>{getSubtitle(result.display_name)}</Typography>
              </Box>
            ))}
            {searchResult.length === 0 && !isSearchLoading && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '12px', borderRadius: '8px' }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 600, color: COLOR.black }}>No result found</Typography>
                <Typography sx={{ fontSize: '14px', color: COLOR.gray[500] }}>Try searching for a different address</Typography>
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  )
}